import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { ATTRACTIONS } from "./Hero";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const PRICES = {
  "standard-visit": { adult: 55, child: 34, senior: 34 },
  "evening-xperience": { adult: 49, child: 28, senior: 28 },
  "priority-access": { adult: 12, child: 12, senior: 12 },
  "hop-on-hop-off": { adult: 64, child: 43, senior: 43 },
};

const TYPES = [
  { id: "adult", label: "Adult", age: "Age Range (13 - 60)" },
  { id: "child", label: "Child", age: "Age Range (3 - 12)" },
  { id: "senior", label: "Senior Citizen", age: "Age Above (61)" },
];

// Значения со скриншотов.
// Для актуальных остатков передавай availability через props.
const SCIENCE_CAPACITY = {
  "09:30": 158,
  "10:00": 156,
  "10:30": 102,
  "11:00": 200,
  "11:30": 200,
  "12:00": 200,
  "12:30": 135,
  "13:00": 188,
  "13:30": 200,
  "14:00": 200,
  "14:30": 200,
  "15:00": 200,
  "15:30": 150,
  "16:00": 50,
};

const SLOT_WARNING =
  "(Kindly be informed that the LAST 2 VISIT SLOTS may provide LESS than 1 to 1 half an hour of visiting time).";

const INCLUSION_SECTIONS = [
  {
    title: "Description",
    subtitle: "General Admission",
    items: [
      "For Malaysians, kindly present MyKad upon request.",
      "Tickets are limited and on first come first served basis.",
      "Petrosains operates on a timed-ticketing system, please be at the entrance or lobby at least 15 minutes before your entry time.",
      "For the safety and comfort of all visitors, CHILDREN aged 12 and below must be accompanied by a parent or guardian throughout their visit at Petrosains The Discovery Centre.",
    ],
  },
  {
    title: "How to use",
    items: [
      "Show Mobile Ticket: Kindly present your printed ticket or e-ticket at the admission point for verification purposes.",
    ],
  },
  {
    title: "Cancellation",
    items: ["No Cancellation and Non-refundable"],
  },
  {
    title: "Terms & Conditions",
    items: [
      "Tickets sold are not refundable or transferable.",
      "This ticket is valid on the date and time of entry as stated on this ticket. The management reserves the right to reschedule visits without prior notice.",
      "The ticket holder as visitor is deemed to have read all instructions, notices, and signs displayed in, on, or around the premises and shall observe and conform to the same.",
      "The management of the operation of the Petrosains, The Discovery Centre, its employees, servants, agents, permitted assigns and/or personal representatives shall not any times whatsoever be responsible or liable, whether expressly or impliedly, for any personal injuries including death, loss and/or damage to personal belongings and/or property or other liabilities whatsoever and howsoever caused including but not limited to the negligence or the management and its employees, servants, agents, permitted assigns and/or personal representatives arising out of or in connection with the Petrosains Visit.",
    ],
  },
];

function readStored(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function dateKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function parseDate(value) {
  if (typeof value !== "string") return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );

  return dateKey(date) === value ? date : null;
}

function buildCalendar(month) {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const offset = (new Date(year, monthIndex, 1).getDay() + 6) % 7;
  const days = new Date(year, monthIndex + 1, 0).getDate();
  const count = Math.ceil((offset + days) / 7) * 7;

  return Array.from({ length: count }, (_, index) => {
    const day = index - offset + 1;

    return day < 1 || day > days
      ? null
      : new Date(year, monthIndex, day);
  });
}

function formatTime(value) {
  const [hour, minute] = value.split(":").map(Number);

  return `${String(hour % 12 || 12).padStart(2, "0")}:${String(
    minute
  ).padStart(2, "0")} ${hour >= 12 ? "PM" : "AM"}`;
}

function defaultSlots(id) {
  const start = 9 * 60 + 30;

  const end =
    id === "standard-visit"
      ? 15 * 60
      : id === "priority-access"
        ? 16 * 60 + 30
        : 16 * 60;

  return Array.from(
    { length: (end - start) / 30 + 1 },
    (_, index) => {
      const minutes = start + index * 30;

      return `${String(Math.floor(minutes / 60)).padStart(
        2,
        "0"
      )}:${String(minutes % 60).padStart(2, "0")}`;
    }
  );
}

function Marker() {
  return (
    <svg
      width="20"
      height="24"
      viewBox="0 0 20 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 11.5 12.7 6a4 4 0 0 1 5.8 3.6v1a4 4 0 0 1-2.2 3.6L5.8 20A4 4 0 0 1 0 16.4v-1.3a4 4 0 0 1 2-3.6Z"
        fill="#78CDC1"
      />
      <path
        d="m5.7 1.7 11 5.2a4 4 0 0 1 1.8 5.3 4 4 0 0 1-5.3 1.8l-11-5.2A4 4 0 0 1 5.7 1.7Z"
        fill="#00AB91"
      />
    </svg>
  );
}

function InclusionDetails() {
  return (
    <div className="sc-inclusion-sections">
      {INCLUSION_SECTIONS.map((section) => (
        <section className="sc-inclusion-section" key={section.title}>
          <h4>{section.title}</h4>

          {section.subtitle && <h4>{section.subtitle}</h4>}

          <ul>
            {section.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function LoadingOverlay() {
  const overlayRef = useRef(null);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const previousPadding = document.body.style.paddingRight;

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    const hiddenElements = [];

    Array.from(document.body.children).forEach((element) => {
      if (
        element === overlayRef.current ||
        !(element instanceof HTMLElement)
      ) {
        return;
      }

      hiddenElements.push({
        element,
        inert: element.inert,
      });

      element.inert = true;
    });

    const currentPadding =
      parseFloat(getComputedStyle(document.body).paddingRight) || 0;

    document.body.style.overflow = "hidden";

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight =
        `${currentPadding + scrollbarWidth}px`;
    }

    overlayRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPadding;

      hiddenElements.forEach(({ element, inert }) => {
        element.inert = inert;
      });

      if (previousFocus?.isConnected) {
        previousFocus.focus?.();
      }
    };
  }, []);

  return createPortal(
    <div
      ref={overlayRef}
      className="sc-loading-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Preparing your booking"
      aria-busy="true"
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === "Tab") event.preventDefault();
      }}
    >
      <div className="sc-loading-dots" role="status">
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />

        <span className="sc-sr-only">
          Loading your booking. Please wait.
        </span>
      </div>
    </div>,
    document.body
  );
}

function DetailsModal({ title, onClose }) {
  const closeRef = useRef(null);
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();

  onCloseRef.current = onClose;

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    function onKeyDown(event) {
      if (event.key === "Escape") {
        onCloseRef.current();
      }

      if (event.key === "Tab") {
        const elements = Array.from(
          dialogRef.current?.querySelectorAll(
            'button:not(:disabled), a[href], [tabindex="0"]'
          ) || []
        );

        const first = elements[0];
        const last = elements[elements.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    }

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus?.();
    };
  }, []);

  return createPortal(
    <div
      className="sc-modal-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="sc-modal"
      >
        <div className="sc-modal-header">
          <div>
            <p className="sc-modal-venue">
              Petrosains, The Discovery Centre
            </p>

            <h3 id={titleId}>{title}</h3>
          </div>

          <button
            ref={closeRef}
            type="button"
            aria-label="Close details"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="sc-rich sc-modal-body" tabIndex={0}>
          <p className="sc-inclusions-label">
            Details &amp; Inclusions
          </p>

          <InclusionDetails />
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Calendar(props) {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [storedBooking] = useState(() => readStored("visitBooking"));
  const [storedSelection] = useState(() =>
    readStored("selectedAttraction")
  );

  const incoming = location.state?.booking;

  const attractionId =
    searchParams.get("attraction") ||
    incoming?.attraction?.id ||
    location.state?.selectedAttraction?.id ||
    storedSelection?.id ||
    storedBooking?.attraction?.id;

  const booking =
    incoming?.attraction?.id === attractionId
      ? incoming
      : storedBooking?.attraction?.id === attractionId
        ? storedBooking
        : null;

  const attraction =
    ATTRACTIONS.find((item) => item.id === attractionId) ||
    booking?.attraction;

  if (!attraction) {
    return (
      <section style={{ padding: 24, textAlign: "center" }}>
        Please select an attraction first.
      </section>
    );
  }

  return (
    <CalendarContent
      key={[
        attractionId,
        booking?.visitDate,
        booking?.residentType,
      ].join(":")}
      {...props}
      attraction={attraction}
      initialBooking={booking}
    />
  );
}

function CalendarContent({
  attraction,
  initialBooking,
  dateStatuses = {},
  availability = {},
  prices: customPrices,
  timeSlots,
  ticketTitle,
}) {
  const navigate = useNavigate();
  const panelId = useId();
  const redirectTimer = useRef(null);
  const submittingRef = useRef(false);

  const prices = {
    ...(PRICES[attraction.id] || {}),
    ...(customPrices || {}),
  };

  const ticketTypes = TYPES.filter((type) =>
    Number.isFinite(prices[type.id])
  );

  const isCombo = attraction.id === "standard-visit";

  const primaryActivity =
    attraction.id === "priority-access" ? "energy" : "science";

  const showAgeRanges = [
    "evening-xperience",
    "priority-access",
  ].includes(attraction.id);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todayKey = dateKey(today);
  const tomorrowKey = dateKey(tomorrow);

  const initialDate = parseDate(initialBooking?.visitDate);
  const validInitialDate =
    initialDate && initialDate >= today ? initialDate : null;

  const [selectedDate, setSelectedDate] = useState(
    validInitialDate ? dateKey(validInitialDate) : ""
  );

  const [visibleMonth, setVisibleMonth] = useState(() => {
    const date = validInitialDate || today;
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  const [expanded, setExpanded] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [selectedTime, setSelectedTime] = useState("");
  const [energyTime, setEnergyTime] = useState("");

  const [quantities, setQuantities] = useState({
    adult: 0,
    child: 0,
    senior: 0,
  });

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (redirectTimer.current !== null) {
        window.clearTimeout(redirectTimer.current);
      }
    };
  }, []);

  const residentType =
    initialBooking?.residentType === "mykad" ? "mykad" : "non-mykad";

  const cells = buildCalendar(visibleMonth);
  const selectedDateObject = parseDate(selectedDate);

  const slots = useMemo(
    () => timeSlots || defaultSlots(attraction.id),
    [timeSlots, attraction.id]
  );

  function getStatus(date) {
    const key = dateKey(date);

    return (
      dateStatuses[key] ||
      (key === initialBooking?.visitDate && initialBooking?.dateStatus) ||
      ([0, 6].includes(date.getDay()) ? "peak" : "offPeak")
    );
  }

  function isAvailable(date) {
    return (
      date >= today &&
      getStatus(date) !== "closed" &&
      availability[dateKey(date)]?.soldOut !== true
    );
  }

  function seatsLeft(date, time, activity) {
    const explicit =
      availability[date]?.activities?.[activity]?.slots?.[time] ??
      (activity === "science" || !isCombo
        ? availability[date]?.slots?.[time]
        : undefined);

    if (Number.isFinite(explicit)) {
      return Math.max(0, Math.floor(explicit));
    }

    return activity === "energy"
      ? 80
      : SCIENCE_CAPACITY[time] ?? 200;
  }

  const hasSelectedTimes = Boolean(
    selectedTime && (!isCombo || energyTime)
  );

  const status = selectedDateObject
    ? getStatus(selectedDateObject)
    : "offPeak";

  const statusLabel =
    status === "peak"
      ? "Peak"
      : status === "publicHoliday"
        ? "Public Holiday"
        : status === "closed"
          ? "Closed"
          : "Non-Peak";

  const title =
    ticketTitle ||
    `${attraction.title} – ${
      residentType === "mykad" ? "MyKad" : "Non-MyKad"
    } (${statusLabel})`;

  const showCustomDate =
    selectedDateObject &&
    selectedDate !== todayKey &&
    selectedDate !== tomorrowKey;

  const totalQuantity = ticketTypes.reduce(
    (sum, type) => sum + quantities[type.id],
    0
  );

  const capacity =
    selectedDate && hasSelectedTimes
      ? Math.min(
          seatsLeft(selectedDate, selectedTime, primaryActivity),
          isCombo
            ? seatsLeft(selectedDate, energyTime, "energy")
            : Infinity
        )
      : 0;

  const total = ticketTypes.reduce(
    (sum, type) => sum + quantities[type.id] * prices[type.id],
    0
  );

  const canGoPrevious =
    visibleMonth.getFullYear() > today.getFullYear() ||
    (visibleMonth.getFullYear() === today.getFullYear() &&
      visibleMonth.getMonth() > today.getMonth());

  function chooseDate(value) {
    if (submittingRef.current) return;

    const date = parseDate(value);

    if (value && (!date || date < today)) {
      setMessage("Please select today or a future date.");
      return;
    }

    if (date && !isAvailable(date)) {
      setMessage("This date is unavailable. Please select another date.");
      return;
    }

    setSelectedDate(value);
    setSelectedTime("");
    setEnergyTime("");
    setQuantities({ adult: 0, child: 0, senior: 0 });
    setMessage("");

    if (date) {
      setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    }

    try {
      localStorage.setItem(
        "visitBooking",
        JSON.stringify({
          ...initialBooking,
          attraction,
          residentType,
          visitDate: value,
          dateStatus: date ? getStatus(date) : null,
          timeSlot: null,
          timeSlots: null,
          quantities: { adult: 0, child: 0, senior: 0 },
        })
      );
    } catch {
      setMessage("Unable to save your selection on this device.");
    }
  }

  function changeMonth(offset) {
    if (submittingRef.current) return;

    setVisibleMonth((current) => {
      const next = new Date(
        current.getFullYear(),
        current.getMonth() + offset,
        1
      );

      const minimum = new Date(today.getFullYear(), today.getMonth(), 1);

      return next < minimum ? minimum : next;
    });
  }

  function selectTime(time, activity = primaryActivity) {
    if (submittingRef.current) return;

    if (isCombo && activity === "energy") {
      setEnergyTime(time);
    } else {
      setSelectedTime(time);
    }

    setQuantities({ adult: 0, child: 0, senior: 0 });
    setMessage("");
  }

  function changeQuantity(id, change) {
    if (submittingRef.current) return;

    if (!hasSelectedTimes) {
      setMessage(
        isCombo
          ? "Please select a time slot for both activities first."
          : "Please select a time slot first."
      );
      return;
    }

    setQuantities((current) => {
      const nextValue = current[id] + change;

      const currentTotal = ticketTypes.reduce(
        (sum, type) => sum + current[type.id],
        0
      );

      if (nextValue < 0 || currentTotal + change > capacity) {
        return current;
      }

      return { ...current, [id]: nextValue };
    });

    setMessage("");
  }

  function handleAddToCart() {
    if (submittingRef.current) return;

    if (!selectedDateObject || !isAvailable(selectedDateObject)) {
      setMessage("Please select an available visit date.");
      return;
    }

    if (!hasSelectedTimes) {
      setMessage(
        isCombo
          ? "Please select a time slot for both activities."
          : "Please select a time slot."
      );
      return;
    }

    if (!totalQuantity) {
      setMessage("Please select at least one ticket.");
      return;
    }

    if (totalQuantity > capacity) {
      setMessage("The selected quantity exceeds available capacity.");
      return;
    }

    const activityTimes = isCombo
      ? {
          science: selectedTime,
          energy: energyTime,
        }
      : null;

    const tickets = ticketTypes
      .filter((type) => quantities[type.id] > 0)
      .map((type) => ({
        id: [
          attraction.id,
          residentType,
          selectedDate,
          selectedTime,
          ...(isCombo ? [energyTime] : []),
          type.id,
        ].join(":"),
        attractionId: attraction.id,
        title: attraction.title,
        ticketType: type.id,
        ticketLabel: type.label,
        residentType,
        visitDate: selectedDate,
        timeSlot: selectedTime,
        ...(isCombo ? { timeSlots: activityTimes } : {}),
        quantity: quantities[type.id],
        price: prices[type.id],
        currency: "MYR",
      }));

    const booking = {
      ...initialBooking,
      attraction,
      residentType,
      visitDate: selectedDate,
      dateStatus: status,
      timeSlot: selectedTime,
      timeSlots: activityTimes,
      quantities: { ...quantities },
      tickets,
      total: Number(total.toFixed(2)),
      currency: "MYR",
    };

    const existing = readStored("selectedTickets");
    const cart = Array.isArray(existing) ? [...existing] : [];

    tickets.forEach((ticket) => {
      const index = cart.findIndex((item) => item.id === ticket.id);

      if (index >= 0) {
        cart[index] = ticket;
      } else {
        cart.push(ticket);
      }
    });

    try {
      localStorage.setItem("selectedTickets", JSON.stringify(cart));
      localStorage.setItem("visitBooking", JSON.stringify(booking));
    } catch {
      setMessage("Unable to save your cart. Please try again.");
      return;
    }

    submittingRef.current = true;
    setMessage("");
    setLoading(true);

    window.dispatchEvent(new Event("cartUpdated"));

    redirectTimer.current = window.setTimeout(() => {
      redirectTimer.current = null;

      navigate("/booking", {
        state: {
          booking,
          tickets,
          selectedTickets: cart,
        },
      });

      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, 3000);
  }

  const activities = isCombo
    ? ["science", "energy"]
    : [primaryActivity];

  return (
    <section className="sc" aria-label="Ticket availability">
      <style>{CSS}</style>

      <div className="sc-container">
        <h2 className="sc-heading">
          <Marker />
          Check Availability
        </h2>

        <div className="sc-date-buttons">
          <button
            type="button"
            className={`sc-date-button ${
              selectedDate === todayKey ? "is-active" : ""
            }`}
            aria-pressed={selectedDate === todayKey}
            onClick={() => chooseDate(todayKey)}
          >
            Today
          </button>

          <button
            type="button"
            className={`sc-date-button ${
              selectedDate === tomorrowKey ? "is-active" : ""
            }`}
            aria-pressed={selectedDate === tomorrowKey}
            onClick={() => chooseDate(tomorrowKey)}
          >
            Tomorrow
          </button>

          {showCustomDate && (
            <div className="sc-custom-date">
              <span>
                {selectedDateObject.toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                })}
              </span>

              <button
                type="button"
                aria-label="Clear selected date"
                onClick={() => chooseDate("")}
              >
                ×
              </button>
            </div>
          )}

          <button
            type="button"
            className="sc-date-button"
            aria-expanded={expanded}
            aria-controls={panelId}
            onClick={() => {
              if (submittingRef.current) return;

              setExpanded(true);
              setMessage("");
            }}
          >
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 17"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1.5 4h13v11h-13zM1.5 7h13M5 1v5M11 1v5"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M4 9h2v2H4zm4 0h2v2H8zm-4 3h2v2H4zm4 0h2v2H8z"
                fill="currentColor"
              />
            </svg>

            Select Date
          </button>
        </div>

        <h2 className="sc-heading sc-ticket-heading">
          <Marker />
          Ticket Options
        </h2>

        <article className="sc-card">
          <div className="sc-card-header">
            <h3>{title}</h3>

            <button
              type="button"
              className={expanded ? "sc-cancel" : "sc-select"}
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={() => {
                if (submittingRef.current) return;

                setExpanded((current) => !current);
                setMessage("");
              }}
            >
              {expanded ? "Cancel" : "Select"}
            </button>
          </div>

          {!expanded && (
            <>
              <button
                type="button"
                className="sc-details-toggle"
                aria-expanded={detailsExpanded}
                onClick={() => setDetailsExpanded((current) => !current)}
              >
                View Details &amp; Inclusions

                <span
                  aria-hidden="true"
                  className={`sc-chevron ${
                    detailsExpanded ? "is-open" : ""
                  }`}
                />
              </button>

              {detailsExpanded && (
                <div className="sc-collapsed-details sc-rich">
                  <InclusionDetails />
                </div>
              )}
            </>
          )}

          <div
            id={panelId}
            className={`sc-reveal ${expanded ? "is-open" : ""}`}
            aria-hidden={!expanded}
          >
            <div className="sc-reveal-inner">
              <div
                className="sc-booking-content"
                style={{ visibility: expanded ? "visible" : "hidden" }}
              >
                <div className="sc-booking-grid">
                  <aside className="sc-sidebar">
                    <div className="sc-sidebar-preview">
                      <p className="sc-sidebar-label">
                        Details &amp; Inclusions
                      </p>

                      <div className="sc-sidebar-content sc-rich">
                        <InclusionDetails />
                      </div>
                    </div>

                    <button
                      type="button"
                      className="sc-view-more"
                      onClick={() => setModalOpen(true)}
                    >
                      View More

                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M6 1H1v5M10 1h5v5M15 10v5h-5M6 15H1v-5"
                          stroke="currentColor"
                          strokeWidth="1.8"
                        />
                      </svg>
                    </button>
                  </aside>

                  <div className="sc-main">
                    <div className="sc-calendar-heading">
                      <h4>Select Visit Date</h4>

                      <div className="sc-legend">
                        <strong>Legend</strong>

                        <span>
                          <i className="sc-dot" />
                          Available
                        </span>

                        <span className="sc-sold-label">
                          <i className="sc-dot sc-dot-red" />
                          Sold Out
                        </span>
                      </div>
                    </div>

                    <div className="sc-month-bar">
                      <div className="sc-month-label">
                        <select
                          aria-label="Select month"
                          value={visibleMonth.getMonth()}
                          onChange={(event) => {
                            const next = new Date(
                              visibleMonth.getFullYear(),
                              Number(event.target.value),
                              1
                            );

                            const minimum = new Date(
                              today.getFullYear(),
                              today.getMonth(),
                              1
                            );

                            setVisibleMonth(
                              next < minimum ? minimum : next
                            );
                          }}
                        >
                          {MONTHS.map((month, index) => (
                            <option key={month} value={index}>
                              {month}
                            </option>
                          ))}
                        </select>

                        <span>{visibleMonth.getFullYear()}</span>
                      </div>

                      <div className="sc-month-arrows">
                        <button
                          type="button"
                          aria-label="Previous month"
                          disabled={!canGoPrevious}
                          onClick={() => changeMonth(-1)}
                        >
                          ‹
                        </button>

                        <button
                          type="button"
                          aria-label="Next month"
                          onClick={() => changeMonth(1)}
                        >
                          ›
                        </button>
                      </div>
                    </div>

                    <div
                      className="sc-calendar"
                      role="group"
                      aria-label={`${
                        MONTHS[visibleMonth.getMonth()]
                      } ${visibleMonth.getFullYear()}`}
                    >
                      {WEEKDAYS.map((day) => (
                        <div className="sc-weekday" key={day}>
                          {day}
                        </div>
                      ))}

                      {cells.map((date, index) => {
                        if (!date) {
                          return (
                            <div
                              key={`empty-${index}`}
                              className="sc-day-empty"
                              aria-hidden="true"
                            />
                          );
                        }

                        const key = dateKey(date);
                        const past = date < today;
                        const closed = getStatus(date) === "closed";
                        const soldOut =
                          availability[key]?.soldOut === true;

                        return (
                          <button
                            key={key}
                            type="button"
                            className={[
                              "sc-day",
                              key === selectedDate ? "is-selected" : "",
                              past || closed ? "is-disabled" : "",
                            ].join(" ")}
                            disabled={past || closed || soldOut}
                            aria-pressed={key === selectedDate}
                            aria-label={`${date.toLocaleDateString(
                              "en-GB",
                              {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              }
                            )}${
                              soldOut
                                ? ", sold out"
                                : past || closed
                                  ? ", unavailable"
                                  : ", available"
                            }`}
                            onClick={() => chooseDate(key)}
                          >
                            <span>{date.getDate()}</span>

                            {!past && !closed && (
                              <i
                                aria-hidden="true"
                                className={`sc-dot ${
                                  soldOut ? "sc-dot-red" : ""
                                }`}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {activities.map((activity) => {
                      const activityTitle =
                        activity === "science"
                          ? "Science Centre Visit"
                          : "Energy Capsule Ride";

                      const showActivityHeading =
                        isCombo || attraction.id === "hop-on-hop-off";

                      const activeTime =
                        isCombo && activity === "energy"
                          ? energyTime
                          : selectedTime;

                      return (
                        <div
                          className="sc-times-section"
                          key={activity}
                        >
                          {showActivityHeading && (
                            <div className="sc-activity-heading">
                              <strong>{activityTitle}</strong>

                              <p>
                                {activity === "science"
                                  ? `Science Centre - ${
                                      residentType === "mykad"
                                        ? "MyKad"
                                        : "Non-MyKad"
                                    } (${statusLabel})`
                                  : "Energy Capsule Ride"}
                              </p>
                            </div>
                          )}

                          <h4>
                            Select Time Slot{" "}
                            <span className="sc-slot-warning">
                              {SLOT_WARNING}
                            </span>
                          </h4>

                          {selectedDateObject &&
                          isAvailable(selectedDateObject) ? (
                            <div className="sc-time-grid">
                              {slots.map((time) => {
                                const left = seatsLeft(
                                  selectedDate,
                                  time,
                                  activity
                                );

                                const active = activeTime === time;

                                return (
                                  <button
                                    key={time}
                                    type="button"
                                    className={`sc-time ${
                                      active ? "is-selected" : ""
                                    }`}
                                    disabled={left === 0}
                                    aria-pressed={active}
                                    aria-label={`${activityTitle}, ${formatTime(
                                      time
                                    )}, ${
                                      left > 0
                                        ? `${left} left`
                                        : "Sold Out"
                                    }`}
                                    onClick={() =>
                                      selectTime(time, activity)
                                    }
                                  >
                                    <strong>
                                      {formatTime(time)}
                                    </strong>

                                    <span>
                                      {left > 0
                                        ? `${left} left`
                                        : "Sold Out"}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="sc-select-date-hint">
                              Please select a date.
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <div className="sc-quantity-section">
                      <h4>Quantity</h4>

                      {ticketTypes.map((type) => (
                        <div
                          key={type.id}
                          className={`sc-quantity-row ${
                            showAgeRanges
                              ? ""
                              : "sc-quantity-row-compact"
                          }`}
                        >
                          <div className="sc-ticket-info">
                            <strong>{type.label}</strong>

                            {showAgeRanges && <span>{type.age}</span>}
                          </div>

                          <strong className="sc-price">
                            MYR {prices[type.id].toFixed(2)}
                          </strong>

                          <div className="sc-counter">
                            <button
                              type="button"
                              className="sc-minus"
                              aria-label={`Remove one ${type.label} ticket`}
                              disabled={
                                !hasSelectedTimes ||
                                quantities[type.id] === 0
                              }
                              onClick={() =>
                                changeQuantity(type.id, -1)
                              }
                            >
                              −
                            </button>

                            <span aria-live="polite">
                              {quantities[type.id]}
                            </span>

                            <button
                              type="button"
                              aria-label={`Add one ${type.label} ticket`}
                              disabled={
                                !hasSelectedTimes ||
                                totalQuantity >= capacity
                              }
                              onClick={() =>
                                changeQuantity(type.id, 1)
                              }
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="sc-cart-footer">
                  <p className="sc-feedback" role="status">
                    {message}
                  </p>

                  <button
                    type="button"
                    className="sc-add-cart"
                    disabled={loading}
                    onClick={handleAddToCart}
                  >
                    Add To Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {!expanded && message && (
          <p className="sc-feedback" role="status">
            {message}
          </p>
        )}
      </div>

      {modalOpen && (
        <DetailsModal
          title={attraction.title}
          onClose={() => setModalOpen(false)}
        />
      )}

      {loading && <LoadingOverlay />}
    </section>
  );
}

const CSS = `
.sc {
  --sc-teal: #00ab91;
  --sc-light: #cdeee8;
  padding: 10px 24px 58px;
  background: #f7f7f7;
  color: #080808;
  font-family: inherit;
  font-size: 16px;
}

.sc *,
.sc-modal-overlay *,
.sc-loading-overlay * {
  box-sizing: border-box;
}

.sc button,
.sc input,
.sc select,
.sc-modal button {
  font: inherit;
}

.sc button,
.sc-modal button {
  cursor: pointer;
}

.sc button:focus-visible,
.sc select:focus-visible,
.sc-modal button:focus-visible,
.sc-modal a:focus-visible {
  outline: 3px solid #007c69;
  outline-offset: 3px;
}

.sc-container {
  max-width: 1240px;
  margin: 0 auto;
}

.sc-heading {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
}

.sc-heading svg {
  flex-shrink: 0;
}

.sc-date-buttons {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  margin: 16px 0 0 10px;
}

.sc-date-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  height: 45px;
  padding: 0 14px;
  border: 2px solid var(--sc-teal);
  border-radius: 6px;
  background: transparent;
  color: var(--sc-teal);
  font-size: 16px;
  font-weight: 600 !important;
  line-height: 21px;
  white-space: nowrap;
}

.sc-date-button.is-active {
  background: var(--sc-teal);
  color: white;
}

.sc-custom-date {
  display: flex;
  align-items: center;
  height: 45px;
  padding-left: 14px;
  border: 2px solid var(--sc-teal);
  border-radius: 6px;
  background: var(--sc-teal);
  color: white;
  font-weight: 700;
}

.sc-custom-date button {
  width: 35px;
  height: 41px;
  border: 0;
  background: none;
  color: inherit;
  font-size: 25px;
  font-weight: 800;
}

.sc-ticket-heading {
  margin-top: 67px;
}

.sc-card {
  margin: 16px 15px 0 22px;
  padding: 0 26px;
  border-radius: 5px;
  background: white;
  box-shadow: 0 2px 4px #0000000f, 0 4px 29px #0000000d;
}

.sc-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  min-height: 109px;
  padding: 30px 14px 21px;
  border-bottom: 1px solid #ddd;
}

.sc-card-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
}

.sc-select,
.sc-add-cart {
  height: 42px;
  padding: 0 20px;
  border: 0;
  border-radius: 6px;
  background: var(--sc-teal);
  color: white;
  font-size: 16px;
  font-weight: 700 !important;
  white-space: nowrap;
}

.sc-select {
  min-width: 90px;
}

.sc-select:hover,
.sc-add-cart:hover {
  background: #009b84;
}

.sc-add-cart:disabled {
  cursor: wait;
}

.sc-cancel {
  height: 36px;
  padding: 0 13px;
  border: 2px solid #aaa;
  border-radius: 5px;
  background: white;
  color: #aaa;
  font-weight: 700 !important;
}

.sc-details-toggle {
  display: flex;
  align-items: center;
  gap: 24px;
  min-height: 64px;
  padding: 16px 11px;
  border: 0;
  background: none;
  color: #62676e;
  text-align: left;
}

.sc-chevron {
  width: 10px;
  height: 10px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: translateY(-3px) rotate(45deg);
}

.sc-chevron.is-open {
  transform: translateY(2px) rotate(225deg);
}

.sc-collapsed-details {
  padding: 0 12px 24px;
}

.sc-reveal {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  visibility: hidden;
  transition:
    grid-template-rows 420ms ease,
    opacity 300ms ease,
    visibility 420ms;
}

.sc-reveal.is-open {
  grid-template-rows: 1fr;
  opacity: 1;
  visibility: visible;
}

.sc-reveal-inner {
  min-height: 0;
  overflow: hidden;
}

.sc-booking-grid {
  display: grid;
  grid-template-columns: minmax(0, 363fr) minmax(0, 759fr);
  align-items: start;
  gap: 34px;
  padding-top: 27px;
}

.sc-sidebar {
  min-width: 0;
}

.sc-sidebar-preview {
  height: 618px;
  overflow: hidden;
  background: #f1faf8;
}

.sc-sidebar-label {
  margin: 0;
  padding: 23px 28px 33px;
  font-size: 16px;
  line-height: 25px;
}

.sc-sidebar-content {
  padding: 8px 30px 0;
}

.sc-sidebar-content h4 {
  margin: 0 0 18px !important;
  font-size: 16px !important;
  font-weight: 700;
}

.sc-sidebar-content li {
  padding-left: 0;
  line-height: 24px;
}

.sc-view-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 56px;
  border: 0;
  background: white;
  color: var(--sc-teal);
  font-weight: 700 !important;
}

.sc-main {
  min-width: 0;
}

.sc-main h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.sc-calendar-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 34px;
}

.sc-legend {
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 12px;
  white-space: nowrap;
}

.sc-legend strong {
  margin-right: 12px;
  font-weight: 700;
}

.sc-legend span {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: #008800;
}

.sc-legend .sc-sold-label {
  color: #ff1515;
}

.sc-dot {
  display: inline-block;
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #008800;
}

.sc-dot-red {
  background: #ff1515;
}

.sc-month-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;
  margin-bottom: 20px;
  min-height: 40px;
}

.sc-month-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 19px;
  line-height: 28px;
}

.sc-month-label select {
  max-width: 150px;
  padding: 0;
  border: 0;
  appearance: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 19px;
}

.sc-month-arrows {
  display: flex;
  overflow: hidden;
  border-radius: 3px;
}

.sc-month-arrows button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 39px;
  padding: 0 0 3px;
  border: 0;
  background: var(--sc-teal);
  color: white;
  font-family: Arial, sans-serif;
  font-size: 34px;
  line-height: 1;
}

.sc-month-arrows button:first-child {
  background: #52c5b5;
}

.sc-month-arrows button:disabled {
  opacity: .5;
  cursor: not-allowed;
}

.sc-calendar {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.sc-weekday {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 31px;
  color: #444;
  font-family: Arial, sans-serif;
  font-size: 14px;
  font-weight: 700;
}

.sc-day,
.sc-day-empty {
  min-width: 0;
  height: 95px;
}

.sc-day-empty {
  background: #f7f7f7;
}

.sc-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 15px;
  padding: 15px 0 0;
  border: 1px solid transparent;
  border-radius: 5px;
  background: white;
  color: #424242;
  font-family: Arial, sans-serif !important;
  font-size: 16px;
  font-weight: 600 !important;
  line-height: 23px;
}

.sc-day:hover:not(:disabled) {
  background: #effaf7;
}

.sc-day.is-selected {
  border-color: var(--sc-teal);
  background: var(--sc-light);
  color: var(--sc-teal);
}

.sc-day.is-disabled {
  justify-content: center;
  padding-top: 0;
  color: #bfbfbf;
}

.sc-day:disabled {
  cursor: default;
}

.sc-times-section {
  margin-top: 27px;
}

.sc-times-section > h4 {
  margin: 0 0 8px 4px;
}

.sc-activity-heading {
  color: #00ab91;
  margin-bottom: 8px;
}

.sc-activity-heading strong {
  font-weight: 600;
}

.sc-activity-heading p {
  margin: 8px 0;
}

.sc-slot-warning {
  color: #ff1515;
  font-weight: 400;
}

.sc-time-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 9px 11px;
}

.sc-time {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 74px;
  padding: 3px 5px 4px;
  border: 1px solid #d7d7d7;
  border-radius: 6px;
  background: white;
  color: #080808;
  font-family: Arial, sans-serif !important;
}

.sc-time strong {
  font-size: 16px;
  font-weight: 700;
  line-height: 23px;
}

.sc-time span {
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
}

.sc-time:hover:not(:disabled) {
  border-color: var(--sc-teal);
  background: #f0faf7;
}

.sc-time.is-selected {
  border-color: var(--sc-teal);
  background: #f1faf8;
  color: var(--sc-teal);
}

.sc-time:disabled {
  color: #b6b6b6;
  background: #fafafa;
  cursor: not-allowed;
}

.sc-select-date-hint {
  display: inline-block;
  padding: 14px 22px;
  border: 1px solid #e1f3ef;
  border-radius: 4px;
  background: #f1faf8;
  line-height: 23px;
}

.sc-quantity-section {
  margin-top: 35px;
  padding: 0 0 15px 3px;
}

.sc-quantity-section > h4 {
  margin-bottom: 14px;
}

.sc-quantity-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 28px;
  min-height: 75px;
}

.sc-quantity-row-compact {
  min-height: 63px;
}

.sc-ticket-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.sc-ticket-info strong {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.sc-ticket-info > span {
  font-size: 12px;
  line-height: 18px;
}

.sc-price {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: .6px;
  white-space: nowrap;
}

.sc-counter {
  display: grid;
  grid-template-columns: 45px 34px 45px;
  align-items: center;
  gap: 13px;
}

.sc-counter button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  padding: 0 0 2px;
  border: 0;
  border-radius: 6px;
  background: var(--sc-teal);
  color: white;
  font-family: Arial, sans-serif !important;
  font-size: 29px;
  font-weight: 700;
  line-height: 1;
}

.sc-counter .sc-minus {
  background: #55c4b3;
}

.sc-counter button:disabled {
  cursor: not-allowed;
}

.sc-counter > span {
  text-align: center;
  font-size: 18px;
}

.sc-cart-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 96px;
  margin: 17px -14px 0;
  padding: 24px 0 18px;
  border-top: 1px solid #e0e0e0;
}

.sc-add-cart {
  margin-left: auto;
  padding-right: 16px;
  padding-left: 16px;
}

.sc-feedback {
  margin: 0;
  color: #53615e;
  font-size: 14px;
  line-height: 22px;
}

.sc-rich {
  font-size: 14px;
  line-height: 1.65;
}

.sc-rich h4 {
  margin: 16px 0 10px;
  font-size: 18px;
  font-weight: 700;
}

.sc-rich p {
  margin: 12px 0;
  white-space: pre-line;
}

.sc-rich ul {
  margin: 12px 0;
  padding-left: 22px;
  list-style: disc;
}

.sc-inclusion-section {
  background: #f1faf8;
  padding: 20px 24px;
  border-radius: 12px;
}

.sc-inclusion-section + .sc-inclusion-section {
  margin-top: 6px;
}

.sc-inclusion-section h4 {
  margin: 0 0 20px;
  font-size: 18px;
  font-weight: 600;
}

.sc-inclusion-section h4 + h4 {
  margin-top: 22px;
}

.sc-inclusion-section ul {
  margin: 0;
  padding-left: 30px;
  list-style: disc;
}

.sc-sidebar-content .sc-inclusion-section {
  padding: 0 0 42px;
  border-radius: 0;
}

.sc-sidebar-content .sc-inclusion-section h4 {
  margin-bottom: 22px !important;
}

.sc-sidebar-content .sc-inclusion-section ul {
  padding-left: 30px;
}

.sc-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #0009;
  font-family: inherit;
}

.sc-modal {
  display: flex;
  flex-direction: column;
  width: min(1425px, 100%);
  max-height: 86vh;
  overflow: hidden;
  padding: 40px 60px 36px;
  border-radius: 12px;
  background: white;
  color: #111;
}

.sc-modal-header {
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
}

.sc-modal-venue {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 400;
}

.sc-modal-header h3 {
  margin: 0;
  font-size: 34px;
  font-weight: 700;
  line-height: 1.25;
}

.sc-modal-header button {
  flex-shrink: 0;
  border: 0;
  background: none;
  color: #808080;
  font-size: 54px;
  font-weight: 700;
  line-height: 1;
}

.sc-modal-body {
  min-height: 0;
  overflow-y: auto;
  padding-right: 18px;
  font-size: 18px;
  line-height: 1.45;
}

.sc-inclusions-label {
  margin: 12px 18px 22px !important;
}

.sc-modal-body .sc-inclusion-section h4 {
  margin: 0 0 22px;
  font-size: 18px;
}

.sc-modal-body .sc-inclusion-section h4 + h4 {
  margin-top: 22px;
}

.sc-modal-body .sc-inclusion-section ul {
  margin: 0;
  padding-left: 50px;
}

.sc-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,.72);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
  cursor: wait;
  outline: none;
  touch-action: none;
  animation: sc-overlay-in .2s ease both;
}

.sc-loading-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 76px;
  height: 40px;
}

.sc-loading-dots > span:not(.sc-sr-only) {
  display: block;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #00ab91;
  animation: sc-dot-pulse .9s ease-in-out infinite;
}

.sc-loading-dots > span:nth-child(2) {
  animation-delay: .15s;
}

.sc-loading-dots > span:nth-child(3) {
  animation-delay: .3s;
}

.sc-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0,0,0,0);
  white-space: nowrap;
  border: 0;
}

@keyframes sc-dot-pulse {
  0%, 80%, 100% {
    transform: scale(.5);
    opacity: .55;
  }

  40% {
    transform: scale(1.08);
    opacity: 1;
  }
}

@keyframes sc-overlay-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (min-width: 1024px) {
  .sc { padding: 10px 24px 44px; font-size: 14px; }
  .sc-container { max-width: 1120px; }
  .sc-heading { gap: 6px; font-size: 20px; line-height: 28px; }
  .sc-heading svg { width: 17px; height: 20px; }
  .sc-date-buttons { gap: 5px; margin: 12px 0 0 8px; }
  .sc-date-button { gap: 7px; height: 39px; padding: 0 12px; font-size: 14px; line-height: 20px; }
  .sc-date-button svg { width: 14px; height: 15px; }
  .sc-custom-date { height: 39px; padding-left: 12px; font-size: 14px; }
  .sc-custom-date button { width: 30px; height: 35px; font-size: 22px; }
  .sc-ticket-heading { margin-top: 46px; }
  .sc-card { margin: 12px 12px 0 18px; padding: 0 22px; }
  .sc-card-header { gap: 20px; min-height: 88px; padding: 24px 10px 18px; }
  .sc-card-header h3 { font-size: 16px; line-height: 23px; }
  .sc-select, .sc-add-cart { height: 36px; padding: 0 16px; font-size: 14px; }
  .sc-select { min-width: 78px; }
  .sc-cancel { height: 32px; padding: 0 11px; font-size: 14px; }
  .sc-details-toggle { gap: 20px; min-height: 52px; padding: 13px 9px; font-size: 14px; }
  .sc-chevron { width: 8px; height: 8px; }
  .sc-collapsed-details { padding: 0 10px 20px; }
  .sc-booking-grid { grid-template-columns: minmax(0, 320fr) minmax(0, 720fr); gap: 26px; padding-top: 22px; }
  .sc-sidebar-preview { height: 520px; }
  .sc-sidebar-label { padding: 18px 22px 25px; font-size: 14px; line-height: 22px; }
  .sc-sidebar-content h4 { font-size: 14px !important; }
  .sc-sidebar-content li { line-height: 21px; }
  .sc-view-more { gap: 5px; min-height: 46px; font-size: 14px; }
  .sc-view-more svg { width: 13px; height: 13px; }
  .sc-main h4 { font-size: 14px; line-height: 22px; }
  .sc-calendar-heading { gap: 12px; min-height: 28px; }
  .sc-legend { gap: 12px; font-size: 11px; }
  .sc-legend strong { margin-right: 8px; }
  .sc-legend span { gap: 8px; }
  .sc-dot { width: 7px; height: 7px; }
  .sc-month-bar { gap: 12px; min-height: 34px; margin-top: 14px; margin-bottom: 15px; }
  .sc-month-label { gap: 5px; font-size: 17px; line-height: 25px; }
  .sc-month-label select { max-width: 140px; font-size: 17px; }
  .sc-month-arrows button { width: 32px; height: 34px; font-size: 29px; }
  .sc-weekday { height: 28px; font-size: 12px; }
  .sc-day, .sc-day-empty { height: 76px; }
  .sc-day { gap: 11px; padding-top: 11px; font-size: 14px; line-height: 21px; }
  .sc-day.is-disabled { padding-top: 0; }
  .sc-times-section { margin-top: 22px; }
  .sc-times-section > h4 { margin: 0 0 8px 3px; }
  .sc-time-grid { gap: 8px 9px; }
  .sc-time { height: 62px; padding: 3px 5px 4px; }
  .sc-time strong { font-size: 14px; line-height: 21px; }
  .sc-time span { font-size: 12px; line-height: 17px; }
  .sc-select-date-hint { padding: 11px 18px; font-size: 14px; line-height: 21px; }
  .sc-quantity-section { margin-top: 27px; padding: 0 0 12px 3px; }
  .sc-quantity-section > h4 { margin-bottom: 11px; }
  .sc-quantity-row { gap: 22px; min-height: 64px; }
  .sc-quantity-row-compact { min-height: 55px; }
  .sc-ticket-info { gap: 3px; }
  .sc-ticket-info strong { font-size: 14px; line-height: 22px; }
  .sc-ticket-info > span { font-size: 11px; line-height: 16px; }
  .sc-price { font-size: 14px; letter-spacing: .4px; }
  .sc-counter { grid-template-columns: 38px 28px 38px; gap: 10px; }
  .sc-counter button { width: 38px; height: 38px; font-size: 25px; }
  .sc-counter > span { font-size: 16px; }
  .sc-cart-footer { gap: 16px; min-height: 78px; margin: 14px -10px 0; padding: 19px 0 15px; }
  .sc-feedback { font-size: 12px; line-height: 20px; }
  .sc-rich { font-size: 13px; line-height: 1.6; }
}

@media (max-width: 900px) {
  .sc-booking-grid { grid-template-columns: minmax(0, 280px) minmax(0, 1fr); gap: 22px; }
  .sc-legend { gap: 9px; font-size: 10px; }
  .sc-legend strong { margin-right: 0; }
  .sc-legend span { gap: 5px; }
  .sc-quantity-row { gap: 12px; }
  .sc-counter { grid-template-columns: 36px 24px 36px; gap: 6px; }
  .sc-counter button { width: 36px; height: 39px; }
  .sc-price { font-size: 13px; }
}

@media (max-width: 700px) {
  .sc { padding: 16px 14px 36px; }
  .sc-heading { font-size: 21px; line-height: 29px; }
  .sc-date-buttons { gap: 6px; margin-left: 0; }
  .sc-date-button { height: 42px; padding: 0 10px; font-size: 14px; }
  .sc-custom-date { height: 42px; padding-left: 10px; font-size: 14px; }
  .sc-custom-date button { height: 38px; }
  .sc-ticket-heading { margin-top: 42px; }
  .sc-card { margin: 16px 0 0; padding: 0 15px; }
  .sc-card-header { gap: 14px; min-height: 99px; padding: 24px 0; }
  .sc-card-header h3 { font-size: 15px; line-height: 23px; }
  .sc-select { min-width: 76px; padding: 0 13px; font-size: 14px; }
  .sc-cancel { padding: 0 9px; font-size: 14px; }
  .sc-details-toggle { gap: 18px; padding-left: 0; font-size: 14px; }
  .sc-booking-grid { grid-template-columns: minmax(0, 1fr); gap: 20px; padding-top: 20px; }
  .sc-sidebar-preview { display: none; }
  .sc-view-more { justify-content: flex-start; min-height: 30px; }
  .sc-calendar-heading { gap: 8px; }
  .sc-calendar-heading h4 { font-size: 14px; }
  .sc-legend { gap: 8px; font-size: 10px; }
  .sc-legend strong { display: none; }
  .sc-month-bar { margin-top: 15px; margin-bottom: 14px; }
  .sc-month-label, .sc-month-label select { font-size: 17px; }
  .sc-weekday { height: 28px; font-size: 11px; }
  .sc-day, .sc-day-empty { height: 67px; }
  .sc-day { gap: 9px; padding-top: 8px; font-size: 14px; }
  .sc-day.is-disabled { padding-top: 0; }
  .sc-dot { width: 6px; height: 6px; }
  .sc-times-section { margin-top: 25px; }
  .sc-time-grid { gap: 8px; }
  .sc-time { height: 70px; }
  .sc-time strong { font-size: 13px; }
  .sc-time span { font-size: 11px; }
  .sc-quantity-section { margin-top: 28px; padding-left: 0; }
  .sc-quantity-row { gap: 10px; min-height: 83px; }
  .sc-quantity-row-compact { min-height: 68px; }
  .sc-ticket-info strong { font-size: 14px; }
  .sc-ticket-info > span { font-size: 10px; }
  .sc-price { font-size: 12px; letter-spacing: 0; }
  .sc-counter { grid-template-columns: 32px 20px 32px; gap: 4px; }
  .sc-counter button { width: 32px; height: 36px; font-size: 24px; }
  .sc-counter > span { font-size: 15px; }
  .sc-cart-footer { flex-wrap: wrap; margin-right: 0; margin-left: 0; padding-top: 20px; }
  .sc-feedback { font-size: 12px; }
  .sc-modal-overlay { padding: 14px; }
  .sc-modal { padding: 22px 18px; }
  .sc-modal-header h3 { font-size: 24px; }
  .sc-modal-venue { font-size: 14px; }
  .sc-modal-header button { font-size: 36px; }
  .sc-modal-body { padding-right: 8px; font-size: 15px; }
  .sc-modal-body .sc-inclusion-section { padding: 18px; }
  .sc-modal-body .sc-inclusion-section ul { padding-left: 22px; }
}

@media (prefers-reduced-motion: reduce) {
  .sc-reveal { transition: none; }
  .sc-loading-overlay { animation: none; }
  .sc-loading-dots > span:not(.sc-sr-only) {
    animation: none;
    opacity: 1;
  }
}
`;